<template>
  <div class="dynamic-form">
    <div class="form-head">
      <div class="t">交代任务</div>
      <div class="d">填得越具体，出来的东西越贴你的产品</div>
    </div>

    <div v-for="f in visibleFields" :key="f.key" class="field">
      <div class="label">
        {{ f.label }}
        <span v-if="f.required" class="req">*</span>
      </div>
      <div v-if="f.hint" class="hint">{{ f.hint }}</div>

      <!-- 图片三槽位：把「为什么值得多传」写在槽位上，而不是写"最多9张" -->
      <div v-if="f.type === 'image-slots'" class="slots">
        <div v-for="s in f.slots" :key="s.key" class="slot">
          <div class="slot-head">
            {{ s.label }}<span v-if="s.required" class="req">*</span>
          </div>
          <div class="slot-hint">{{ s.hint }}</div>
          <div class="thumbs">
            <div v-for="(url, i) in slotImages[s.key]" :key="i" class="thumb">
              <el-image :src="url" fit="cover" />
              <i class="el-icon-close del" @click="removeImage(s.key, i)"></i>
            </div>
            <div class="thumb add" :class="{ busy: uploading[s.key] }" @click="pickFile(s.key)">
              <i v-if="uploading[s.key]" class="el-icon-loading"></i>
              <i v-else class="el-icon-plus"></i>
            </div>
          </div>
          <!-- ★每个槽位各自一个 input：共用一个 input 靠 activeSlot 记状态太脆弱，
               而且没法被自动化直接投递文件 -->
          <input
            :ref="'fi_' + s.key"
            type="file"
            accept="image/*"
            multiple
            class="hidden-input"
            @change="onFileChange($event, s.key)"
          />
        </div>
      </div>

      <el-input
        v-else-if="f.type === 'textarea'"
        v-model="model[f.key]"
        type="textarea"
        :rows="2"
        :placeholder="f.placeholder || ''"
      />

      <div v-else-if="f.type === 'slider'" class="slider-row">
        <el-switch v-model="pageAuto" active-text="让 AI 决定" />
        <el-slider v-if="!pageAuto" v-model="model[f.key]" :min="f.min || 3" :max="f.max || 9" show-stops class="slider" />
      </div>

      <div v-else-if="f.type === 'radio-image'" class="styles">
        <div
          class="style-chip"
          :class="{ on: !model[f.key] }"
          @click="model[f.key] = ''"
        >AI 决定</div>
        <div
          v-for="o in f.options"
          :key="o.value"
          class="style-chip"
          :class="{ on: model[f.key] === o.value }"
          @click="model[f.key] = o.value"
        >{{ o.label }}</div>
      </div>

      <el-switch v-else-if="f.type === 'switch'" v-model="model[f.key]" />

      <el-input v-else v-model="model[f.key]" :placeholder="f.placeholder || ''" />
    </div>

    <div class="submit-row">
      <el-button type="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">
        开始生成<template v-if="pointsCost"> · 消耗 {{ pointsCost }} 点</template>
      </el-button>
      <span v-if="!canSubmit" class="tip">至少上传 1 张正面主图</span>
    </div>
  </div>
</template>

<script>
import { uploadImgApi } from '@/apis/user'

export default {
  name: 'DynamicForm',
  props: {
    /** 后端下发的表单定义 —— ★前端不写死字段，新增工作流不用改前端 */
    schema: { type: Object, default: () => ({ fields: [] }) },
    pointsCost: { type: Number, default: 0 },
    submitting: { type: Boolean, default: false },
  },

  data() {
    return {
      model: {},
      slotImages: {},   // { front: [url...], back: [...], detail: [...] }
      uploading: {},
      pageAuto: true,   // 张数默认交给 AI
    }
  },

  computed: {
    fields() {
      return (this.schema && this.schema.fields) || []
    },
    /**
     * 实际渲染出来的字段。
     * ★`hidden` 只藏界面、不藏数据 —— 字段仍然进 model，它的 default 照样会被提交。
     *   这样"先不给用户选、但默认值要生效"这种需求不用改代码，改后端 schema 一个布尔就行；
     *   把字段整个删掉则会连选项定义一起丢，将来想开放得重写一遍。
     */
    visibleFields() {
      return this.fields.filter((f) => !f.hidden)
    },
    allImages() {
      // ★正面图排最前：genScene 挑参考图时顺序有意义
      const order = ['front', 'back', 'detail']
      return order.reduce((acc, k) => acc.concat(this.slotImages[k] || []), [])
    },
    canSubmit() {
      return (this.slotImages.front || []).length > 0
    },
  },

  created() {
    this.initModel()
  },

  methods: {
    initModel() {
      const model = {}
      const slots = {}
      const uploading = {}
      this.fields.forEach((f) => {
        if (f.type === 'image-slots') {
          ;(f.slots || []).forEach((s) => {
            this.$set(slots, s.key, [])
            this.$set(uploading, s.key, false)
          })
        } else if (f.type === 'switch') {
          model[f.key] = !!f.default
        } else if (f.type === 'slider') {
          model[f.key] = f.min || 3
        } else {
          model[f.key] = ''
        }
      })
      this.model = model
      this.slotImages = slots
      this.uploading = uploading
    },

    pickFile(slotKey) {
      if (this.uploading[slotKey]) return
      const r = this.$refs['fi_' + slotKey]
      const input = Array.isArray(r) ? r[0] : r
      if (input) {
        input.value = '' // 允许重复选同一个文件
        input.click()
      }
    },

    async onFileChange(ev, slotKey) {
      const files = Array.from(ev.target.files || [])
      if (!files.length) return

      // 前端先卡一道，省得传上去再被后端拒
      const tooBig = files.find((f) => f.size > 10 * 1024 * 1024)
      if (tooBig) {
        this.$message.warning('单张图片请控制在 10MB 以内')
        return
      }
      if (this.allImages.length + files.length > 9) {
        this.$message.warning('最多一共 9 张图')
        return
      }

      this.$set(this.uploading, slotKey, true)
      try {
        for (const file of files) {
          const res = await uploadImgApi(file)
          if (res && res.code === 0 && res.data) {
            this.slotImages[slotKey].push(res.data)
          } else {
            this.$message.error((res && res.msg) || '图片上传失败')
          }
        }
      } catch (e) {
        this.$message.error('图片上传失败，请重试')
      } finally {
        this.$set(this.uploading, slotKey, false)
      }
    },

    removeImage(slotKey, index) {
      this.slotImages[slotKey].splice(index, 1)
    },

    submit() {
      const data = { ...this.model, imagePaths: this.allImages }
      // 张数交给 AI 时就不传这个字段（后端 optional，不传即 AI 决定）
      if (this.pageAuto) delete data.totalPages
      this.$emit('submit', data)
    },
  },
}
</script>

<style lang="scss">
.dynamic-form {
  max-width: 720px;
  margin: 0 auto;
  padding: 30px 36px 60px;

  .form-head {
    margin-bottom: 22px;
    .t { font-size: 19px; font-weight: 600; color: #1f2329; }
    .d { margin-top: 5px; font-size: 12px; color: #8a8f98; }
  }

  .field { margin-bottom: 22px; }
  .label { font-size: 14px; color: #1f2329; font-weight: 500; }
  .req { color: #f56c6c; margin-left: 3px; }
  .hint { margin: 4px 0 8px; font-size: 12px; color: #8a8f98; line-height: 1.6; }

  .slots { display: flex; gap: 14px; flex-wrap: wrap; }
  .slot {
    flex: 1;
    min-width: 200px;
    padding: 12px;
    border: 1px dashed #dcdfe6;
    border-radius: 10px;
    .slot-head { font-size: 13px; color: #1f2329; }
    .slot-hint { margin-top: 3px; font-size: 11px; color: #a0a4ab; line-height: 1.5; min-height: 32px; }
  }
  .thumbs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
  .thumb {
    position: relative;
    width: 56px; height: 56px;
    border-radius: 6px; overflow: hidden;
    .el-image { width: 100%; height: 100%; }
    .del {
      position: absolute; right: 0; top: 0;
      background: rgba(0, 0, 0, 0.55); color: #fff;
      font-size: 12px; padding: 2px; cursor: pointer; border-radius: 0 0 0 6px;
    }
  }
  .thumb.add {
    display: flex; align-items: center; justify-content: center;
    border: 1px dashed #c3c7ce; color: #a0a4ab; cursor: pointer;
    &:hover { border-color: #4b7bff; color: #4b7bff; }
    &.busy { cursor: wait; }
  }
  .hidden-input { display: none; }

  .slider-row { display: flex; align-items: center; gap: 18px; }
  .slider { flex: 1; max-width: 320px; }

  .styles { display: flex; flex-wrap: wrap; gap: 8px; }
  .style-chip {
    padding: 6px 14px; border: 1px solid #dcdfe6; border-radius: 16px;
    font-size: 13px; color: #606266; cursor: pointer;
    &:hover { border-color: #4b7bff; color: #4b7bff; }
    &.on { border-color: #4b7bff; color: #fff; background: #4b7bff; }
  }

  .submit-row { margin-top: 30px; display: flex; align-items: center; gap: 12px; }
  .tip { font-size: 12px; color: #b0b4bb; }
}
</style>
